import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import TextStep from './TextStep';
import { Message } from './types';
import { styles } from './ChatMessage.styles';
import { useChatScroll } from './hooks/useChatScroll';
import MarkdownViewer from "./MarkdownViewer";

const MESSAGE_TYPES = {
  USER: 'user',
  LIA: 'lia'
} as const;

interface ChatMessageProps {
  msg: Message;
  isLastLiaMessage: boolean;
  onDone?: () => void;
  onWordTyped?: () => void;
  isCompleted?: boolean;
  isFloatingBall?: boolean;
}

const MessageContent: React.FC<{ text: string | React.ReactNode }> = ({ text }) => (
  <Typography
    variant="body2"
    sx={styles.messageText}
  >
    {text}
  </Typography>
);

const MessageTime: React.FC<{ time: string }> = ({ time }) => (
  <Typography
    variant="caption"
    sx={styles.messageTime}
  >
    {time}
  </Typography>
);

const ChatMessage: React.FC<ChatMessageProps> = ({
  msg,
  isLastLiaMessage,
  onDone,
  onWordTyped,
  isCompleted = false,
  isFloatingBall = false
}) => {
  const isUserMessage = msg.from === MESSAGE_TYPES.USER;
  const { setTyping } = useChatScroll(isFloatingBall);

  const textStep = useMemo(() => {
    if (msg.from === MESSAGE_TYPES.LIA && isLastLiaMessage && typeof msg.text === 'string') {
      return (
        <TextStep
          texto={msg.text}
          onDone={() => {
            setTyping(false);
            onDone?.();
          }}
          onWordTyped={() => {
            setTyping(true);
            onWordTyped?.();
          }}
          isCompleted={isCompleted}
        />
      );
    }
    return msg.text;
  }, [msg.text, msg.from, isLastLiaMessage, onDone, onWordTyped, isCompleted, setTyping]);

  return (
      <Box sx={styles.messageContainer(isUserMessage)}>
        <Box sx={styles.messageBubble(isUserMessage)}>
          {msg.from === MESSAGE_TYPES.LIA && isLastLiaMessage ? (
              <TextStep
                  texto={msg.text as string}
                  onDone={() => {
                    setTyping(false);
                    onDone?.();
                  }}
                  onWordTyped={() => {
                    setTyping(true);
                    onWordTyped?.();
                  }}
                  isCompleted={isCompleted}
              />
          ) : (
              <MarkdownViewer content={msg.text as string} />
          )}
          <MessageTime time={msg.time} />
        </Box>
      </Box>

  );
};

export default React.memo(ChatMessage);
